import { getToken } from "./auth-token";

// Mirrors Frontend/app.js's api() -- same error contract ({ detail: string } on non-2xx,
// per FastAPI's default HTTPException body) and the same auth header shape, so a
// backend error reads identically in both frontends.
//
// No same-origin fallback: an empty API_BASE means every call resolves to this app's
// OWN origin, which Firebase Hosting's SPA rewrite answers with index.html (200, HTML)
// for any path including /api/*. apiFetch would then "succeed" with an empty object
// instead of the real response -- exactly the failure mode that shipped once already
// (a build run without VITE_API_BASE_URL set). Failing at import time is louder but
// correct; a silently-wrong API base is worse than a build that refuses to start.
export const API_BASE = (() => {
  const configured = import.meta.env.VITE_API_BASE_URL;
  if (!configured && !import.meta.env.DEV) {
    throw new Error(
      "VITE_API_BASE_URL is not set. Every /api/* call would otherwise resolve to this app's own origin instead of the backend.",
    );
  }
  return (configured || "").replace(/\/+$/, "");
})();

// WHICH ACCOUNT this tab is acting in, as the 20-character public id from the URL.
//
// Module-level rather than threaded through every apiFetch call, because it is a property
// of the TAB, not of any one request — three hundred call sites would otherwise each have
// to remember to pass it, and the one that forgot would silently read the wrong account's
// data. AccountProvider is the only writer.
//
// Null means "no account chosen yet" and the header is omitted, which the server reads as
// "their own" — the correct answer for a login that has not landed anywhere yet.
let currentAccountId: string | null = null;

export function setAccountHeader(accountId: string | null): void {
  currentAccountId = accountId;
}

export function getAccountHeader(): string | null {
  return currentAccountId;
}

export class ApiError extends Error {
  readonly status: number;
  /**
   * The parsed response body, verbatim.
   *
   * A flattened message is enough for a toast and not enough for a form: an endpoint that
   * answers "here are the six things wrong with your graph" has that list thrown away by
   * the time the caller sees `message`. Carrying the body lets a caller that knows the
   * shape read it, while every existing caller keeps using `message` untouched.
   */
  readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// FastAPI sends `detail` as a STRING for HTTPException, but as a LIST of
// {loc, msg, type} objects for 422 validation failures. Typing it as a string made a
// validation error render as "[object Object]" everywhere an error message is shown.
interface ValidationIssue {
  msg?: unknown;
}

interface ApiErrorBody {
  detail?: unknown;
}

export function readDetail(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const detail = (data as ApiErrorBody).detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const messages = (detail as ValidationIssue[])
      .map((issue) => (typeof issue?.msg === "string" ? issue.msg : null))
      .filter((msg): msg is string => msg !== null);
    if (messages.length > 0) return messages.join(". ");
  }

  // A dict detail, e.g. HTTPException(422, {"problems": [...]}). Without this the message
  // falls through to res.statusText and an operator reads "Unprocessable Entity" while the
  // server already knew exactly which steps were broken.
  const problems = (detail as { problems?: unknown } | null)?.problems;
  if (Array.isArray(problems)) {
    const listed = problems.filter((p): p is string => typeof p === "string");
    if (listed.length > 0) return listed.join(" ");
  }

  // A coded detail that carries its own sentence: the calls API answers every refusal as
  // {"code": "...", "message": "...", "meta_code": ...}. The code is for the caller to branch
  // on (read it off ApiError.body); the message is what a person reads.
  const message = (detail as { message?: unknown } | null)?.message;
  if (typeof message === "string" && message.trim()) return message;

  return undefined;
}

/** The `problems` list from a 422, when the endpoint sent one. */
export function readProblems(error: unknown): string[] | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const detail = (error.body as { detail?: { problems?: unknown } } | null)?.detail;
  const problems = detail?.problems;
  if (!Array.isArray(problems)) return undefined;
  const listed = problems.filter((p): p is string => typeof p === "string");
  return listed.length > 0 ? listed : undefined;
}

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export async function apiFetch<TResponse>(
  path: string,
  // `accountId` reads ONE call as another account -- the platform admin's tenant page shows a
  // tenant's own cards this way. Every other call leaves it out and sends the current account.
  init: { method?: HttpMethod; body?: unknown; accountId?: string | undefined } = {},
): Promise<TResponse> {
  const { method = "GET", body, accountId } = init;
  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = isFormData ? {} : { "Content-Type": "application/json" };

  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  // An account the caller was not granted is ignored server-side rather than refused —
  // see rbac.resolve — so a stale header degrades to their own data, never to a 403 they
  // cannot clear.
  const account = accountId ?? currentAccountId;
  if (account) headers["X-Account-Id"] = account;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: isFormData ? body : body != null ? JSON.stringify(body) : null,
  });

  // A non-JSON body (e.g. an HTML error page, or a misrouted request answered by a
  // static-hosting SPA rewrite) must surface as a clear error, not silently become {}
  // and let the caller destructure fields off an empty object three lines later.
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new ApiError(res.status, `Expected JSON from ${path} but the response body wasn't valid JSON.`);
  }

  if (!res.ok) {
    throw new ApiError(res.status, readDetail(data) ?? res.statusText, data);
  }

  return data as TResponse;
}

/**
 * GET a file the API serves (inbox media) as a Blob, with the SAME Bearer and
 * X-Account-Id headers apiFetch sends. An <img src> pointed at the API would send neither,
 * which is why a file is fetched here and shown through an object URL instead.
 */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (currentAccountId) headers["X-Account-Id"] = currentAccountId;

  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) {
    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = undefined;
    }
    throw new ApiError(res.status, readDetail(data) ?? res.statusText, data);
  }
  return res.blob();
}

